import { useMemo, useState } from 'react';

import {
  HttpError,
  LinearProgress,
  useNotify,
  useRecordContext,
  useRefresh
} from 'react-admin';

import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { Box, Button, Tooltip } from '@mui/material';

import { ContentItem } from 'resources/activities/models/ContentItem.model';
import { ResourceName } from 'shared/enums';
import { ContentUpdateAction } from 'shared/enums/content-enums';
import { useAxiosCall } from 'shared/hooks/useAxiosCall';

import { getTitleCase } from 'shared/utils/helperFunctions/strings';

import { px } from 'shared/utils/styles/css-unit';

import { EditDateFormDialog } from './EditDateFormDialog.component';

export function EditTrendingTagButton(props: { label?: boolean }) {
  const [shouldShowConfirm, setShouldShowConfirm] = useState<boolean>();
  const record = useRecordContext<ContentItem>();
  const refresh = useRefresh();
  const notify = useNotify();
  const tooltip = `Update trending tag`;

  const isTrendingTagEnabled = useMemo<boolean>(() => {
    const newDate = new Date(record.trending_until);
    const today = new Date();
    return newDate > today;
  }, [record?.trending_until]);

  const fetchTrendingUntil = useAxiosCall<boolean>({
    resource: `${ResourceName.BASIC_CONTENT}/${record.id}`,
    autoFetch: false,
    init: { method: 'PATCH' }
  });

  const handleConfirm = (trending_until: string) => {
    fetchTrendingUntil
      .fetch({ trending_until, action: ContentUpdateAction.Tags })
      .then(() => handleSuccess())
      .catch((err) => handleFail(err));
  };

  const handleSuccess = () => {
    setShouldShowConfirm(false);
    refresh();
    notify(`Trending tag updated successfully`, { type: 'success' });
  };

  const handleFail = (e: HttpError) => {
    setShouldShowConfirm(false);
    notify(e.message, { type: 'error' });
  };

  const handleClick = () => {
    setShouldShowConfirm(true);
  };

  const handleAbort = () => {
    setShouldShowConfirm(false);
  };

  if (record == null) {
    return <LinearProgress />;
  }

  return (
    <>
      <Tooltip
        arrow
        placement='left'
        title={tooltip}
        describeChild
        enterDelay={1000}
      >
        <div>
          <Button
            onClick={handleClick}
            sx={(theme) => ({
              px: px(5),
              py: px(4),
              color: theme.palette.primary.main,
              lineHeight: 1.5
            })}
          >
            <TrendingUpIcon
              fontSize='small'
              color={isTrendingTagEnabled ? undefined : 'disabled'}
            />
            {props.label != null ? (
              <Box component='span' sx={{ pl: px(6) }}>
                {'Trending Tag'}
              </Box>
            ) : (
              ''
            )}
          </Button>
        </div>
      </Tooltip>
      <EditDateFormDialog
        onClose={handleAbort}
        onSend={handleConfirm}
        loading={fetchTrendingUntil.loading}
        isOpen={shouldShowConfirm}
        defaultValue={record?.trending_until}
        content={`Confirm to change ${getTitleCase(record.type)} trending tag`}
        title={`Update trending tag`}
        label={'Trending Until'}
      />
    </>
  );
}
